// secure.ts
const SECRET = "app_secure_v1";

const te = new TextEncoder();
const td = new TextDecoder();

const hasCrypto = () =>
  typeof globalThis !== "undefined" &&
  !!globalThis.crypto &&
  !!globalThis.crypto.subtle &&
  typeof globalThis.crypto.getRandomValues === "function";

const b64e = (bytes: Uint8Array) => {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
};

const b64d = (b64: string) => {
  const s = atob(b64);
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return bytes;
};

const getKey = async () => {
  const km = await crypto.subtle.importKey(
    "raw",
    te.encode(SECRET),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: te.encode("app_store_salt_v1"),
      iterations: 120000,
      hash: "SHA-256",
    },
    km,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

export const secureStorage = {
  getItem: async (name: string): Promise<string | null> => {
    const raw = localStorage.getItem(name);
    if (!raw) return null;
    if (!hasCrypto()) return raw;

    try {
      const parsed = JSON.parse(raw);
      if (!parsed?.iv || !parsed?.value) return null;

      const key = await getKey();
      const iv = b64d(parsed.iv);
      const value = b64d(parsed.value);

      const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv },
        key,
        value
      );
      return td.decode(decrypted);
    } catch {
      return null;
    }
  },

  setItem: async (name: string, value: string): Promise<void> => {
    if (!hasCrypto()) {
      localStorage.setItem(name, value);
      return;
    }

    const key = await getKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      te.encode(value)
    );

    localStorage.setItem(
      name,
      JSON.stringify({
        iv: b64e(iv),
        value: b64e(new Uint8Array(encrypted)),
      })
    );
  },

  removeItem: async (name: string): Promise<void> => {
    localStorage.removeItem(name);
  },
};
